import { Inject, Injectable } from '@angular/core';
import { AlertService } from '@ddv/common-components';
import { TrebekQueryMappingService } from '@ddv/datasets';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { ExportDatasetInfo, ExportFormat, ExportWidgetInfo, TrebekConversationFields } from '@ddv/models';
import { handleFileDownload } from '@ddv/utils';
import { format } from 'date-fns';

import { ExportData, ExportWidgetColumn, ExportWidgetData, FileExportData, FilteredExportData, FullExportData } from '../models/dashboard-export';
import {
    createSummaryRow,
    getColumnData,
    getMappedMetadata,
    getRowData,
} from '../utils/export-helpers';

const ZIP_NAME_DATE_FORMAT = 'ddMMyyyy';
const ALERT_TIMEOUT = 500;

@Injectable({
    providedIn: 'root',
})
export class DashboardExportService {
    private alertMessageTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        @Inject(ApiServices.ddvApi) private readonly ddvApiExecutor: ApiExecutorService,
        private readonly alertService: AlertService,
        private readonly trebekQueryMappingService: TrebekQueryMappingService,
    ) {
    }

    exportFullDashboard(
        datasets: ExportDatasetInfo[],
        dashboardName: string,
        exportWidgetInfos: ExportWidgetInfo[],
        exportFormat: ExportFormat,
        clientCode: string,
    ): void {
        this.scheduleAlert();
        const exportUrl = `/dashboards/export/${exportFormat.toLowerCase()}/full`;
        const fullExportBody = this.buildFullExportBody(datasets, dashboardName, exportWidgetInfos, exportFormat);
        this.ddvApiExecutor.invokeServiceWithBodyAndReturnFile(clientCode, exportUrl, fullExportBody).subscribe({
            next: (response) => {
                clearTimeout(this.alertMessageTimer);
                // We can remove this line once all DDV environments start living in AWS and there are no on-prem ones
                const filenameToUseIfNotReturnedWithResponse = this.generateFilename(fullExportBody, exportFormat);
                handleFileDownload(response, filenameToUseIfNotReturnedWithResponse);
            },
            error: (error) => {
                clearTimeout(this.alertMessageTimer);
                console.error(error);
                this.alertService.error('Export failed');
            },
        });
    }

    exportFilteredDashboard(
        datasets: ExportDatasetInfo[],
        dashboardName: string,
        exportWidgetInfos: Partial<ExportWidgetInfo>[],
        exportFormat: ExportFormat,
        clientCode: string,
    ): void {
        this.scheduleAlert();
        const exportUrl = `/dashboards/export/${exportFormat.toLowerCase()}/filtered`;
        const filteredExportBody = this.buildFilteredExportBody(datasets, dashboardName, exportWidgetInfos, exportFormat);
        this.ddvApiExecutor.invokeServiceWithBodyAndReturnFile(clientCode, exportUrl, filteredExportBody).subscribe({
            next: (response) => {
                clearTimeout(this.alertMessageTimer);
                // We can remove this line once all DDV environments start living in AWS and there are no on-prem ones
                const filenameToUseIfNotReturnedWithResponse = this.generateFilename(filteredExportBody, exportFormat);
                handleFileDownload(response, filenameToUseIfNotReturnedWithResponse);
            },
            error: (error) => {
                clearTimeout(this.alertMessageTimer);
                console.error(error);
                this.alertService.error('Export failed');
            },
        });
    }

    private buildFullExportBody(
        datasets: ExportDatasetInfo[],
        dashboardName: string,
        exportWidgetInfos: ExportWidgetInfo[],
        exportFormat: ExportFormat,
    ): FullExportData {
        return {
            dashboardName,
            exportData: this.prepareFullExportData(datasets, exportWidgetInfos, exportFormat),
        };
    }

    private buildFilteredExportBody(
        datasets: ExportDatasetInfo[],
        dashboardName: string,
        exportWidgetInfos: Partial<ExportWidgetInfo>[],
        exportFormat: ExportFormat,
    ): FilteredExportData {
        return {
            dashboardName,
            exportData: this.prepareFilteredExportData(datasets, exportWidgetInfos, exportFormat),
        };
    }

    private scheduleAlert(): void {
        this.alertMessageTimer = setTimeout(() => {
            this.alertService.message('Your export has started.');
        }, ALERT_TIMEOUT);
    }

    private prepareFullExportData(
        datasets: ExportDatasetInfo[],
        exportWidgetInfos: ExportWidgetInfo[],
        exportFormat: ExportFormat,
    ): ExportData[] {
        return datasets.map((ds) => {
            return {
                queryId: this.getQueryId(ds.widgets[0].widgetId),
                widgetData: this.getExportWidgetData(ds, exportWidgetInfos, exportFormat),
            };
        });
    }

    private prepareFilteredExportData(
        datasets: ExportDatasetInfo[],
        exportWidgetInfos: Partial<ExportWidgetInfo>[],
        exportFormat: ExportFormat,
    ): FileExportData[] {
        return datasets.reduce((data: FileExportData[], dataset) => {
            const name = exportWidgetInfos.find((w) => w.id === Number(dataset.widgets[0].widgetId))?.name ?? '';

            const mappedMetadata = getMappedMetadata(dataset);
            const columnData = getColumnData(dataset.data[0] ?? {}, dataset.widgets[0].columnNames, mappedMetadata);
            const columns = exportFormat === 'EXCEL' ?
                columnData.map((c) => c.columnName) :
                [...new Set(columnData.map((c) => c.columnName))].map((c) => c.replace('_crosstalk_key', ''));

            const grouperData = exportFormat === 'EXCEL' ?
                getColumnData(dataset.data[0] ?? {}, dataset.widgets[0].groupers ?? [], mappedMetadata) :
                [];

            const rowData = getRowData(dataset, grouperData, columnData, exportFormat, columns);

            if (exportFormat === 'CSV') {
                const emptyRow = {};
                rowData.push(emptyRow);

                const summaryRow = createSummaryRow(columnData, dataset);
                rowData.push(summaryRow);
            }

            const groupers = exportFormat === 'EXCEL' ? dataset.widgets[0].groupers ?? [] : [];
            const summary = dataset.summary;
            const dataTypeOverrides = exportFormat === 'EXCEL' ? dataset.widgets[0].dataTypeOverrides ?? {} : {};

            data.push({ name, rowData, columns, groupers, summary, dataTypeOverrides });
            return data;
        }, []);
    }

    private getQueryId(widgetId: string): string {
        return this.trebekQueryMappingService.getTrebekQueryIdWidgetMappings().find((q) => q.widgetIds.includes(Number(widgetId)))?.queryId ?? '';
    }

    private getExportWidgetData(
        datasetInfo: ExportDatasetInfo,
        exportWidgetInfos: ExportWidgetInfo[],
        exportFormat: ExportFormat,
    ): ExportWidgetData {
        const widgetId = Number(datasetInfo.widgets[0].widgetId);
        const widgetName = exportWidgetInfos.find((w) => w.id === widgetId)?.name ?? '';
        const columns = this.getColumnsForExportedWidgets(datasetInfo, exportFormat);
        const dataTypeOverrides = datasetInfo.widgets[0].dataTypeOverrides ?? {};

        return { widgetName, widgetId, columns, dataTypeOverrides };
    }

    private getColumnsForExportedWidgets(datasetInfo: ExportDatasetInfo, exportFormat: ExportFormat): ExportWidgetColumn[] {
        const dateColumnsValues: { [key: string]: string | undefined } = {
            'From Date': datasetInfo.widgets[0].dateFrom,
            'To Date': datasetInfo.widgets[0].dateTo,
            'Time of Extract': datasetInfo.widgets[0].extractionDateTime,
        };

        const columnIdMap: { [key: string]: string } = {
            attachments: TrebekConversationFields.AttachmentsCount,
        };

        return [...new Set(datasetInfo.widgets[0].columnNames)].map((column) => {
            const columnName = datasetInfo.crosstalkFields ? column.replace('_crosstalk_key', '') : column;

            if (columnIdMap[columnName]) {
                return {
                    id: columnIdMap[columnName],
                    displayName: columnName,
                };
            }

            const displayName = datasetInfo.metadata ?
                Object.values(datasetInfo.metadata).find((mdv) => mdv.name === columnName)?.displayName ?? '' :
                '';

            return exportFormat === 'CSV' ?
                {
                    id: columnName,
                    displayName,
                    ...(dateColumnsValues[columnName] && { value: dateColumnsValues[columnName] }),
                } :
                {
                    id: columnName,
                    displayName,
                };
        });
    }

    private generateFilename(exportBody: FullExportData | FilteredExportData, exportFormat: ExportFormat): string {
        if (exportBody.exportData.length > 1) {
            return `${exportBody.dashboardName}_${format(new Date(), ZIP_NAME_DATE_FORMAT)}.zip`;
        }

        const exportDatum = exportBody.exportData[0];

        if ('widgetData' in exportDatum) {
            if (exportFormat === 'CSV') {
                return `${exportDatum.widgetData.widgetName}-Full.csv`;
            }
            return `${exportDatum.widgetData.widgetName}-Full.xlsx`;
        }

        if (exportFormat === 'CSV') {
            return `${exportDatum.name}.csv`;
        }
        return `${exportDatum.name}.xlsx`;
    }
}
